/**
 * 积分明细页（积分系统开发方案 v1.10 七节，P1 新建）：
 * - 全屏 createPortal 到 body / z-[250] / #121212 整页（对齐 MembershipModal 先例），
 *   ESC 关闭 + body 滚动锁定；不引入新路由，入口 = UserMenu「积分明细」菜单项；
 * - 数据 = GET /api/credits/ledger（creditsApi.getLedger，JWT 鉴权分页），
 *   ledgerType 服务端等值筛选（空 = 全部，切换时重置回第 1 页）；
 * - 每行：类型标签 + 变动原因/模型 + 时间 + 变动额（正绿负红）+ 变动后余额（balanceAfter）；
 * - 顶栏展示当前总余额（points + dailyPoints，与后端总余额口径一致），随查询刷新。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useAuthStore } from '../../../stores/authStore';
import { creditsApi, LedgerItem } from '../../../services/creditsApi';

/** 服务端 ledger_type 等值筛选选项（值集 = services/credits.py 实际写入类型；'' = 全部） */
const LEDGER_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'consume', label: '消费' },
  { value: 'refund', label: '回补' },
  { value: 'subscribe_grant', label: '订阅发放' },
  { value: 'daily_grant', label: '每日赠送' },
  { value: 'expire', label: '过期清零' },
  { value: 'daily_expire', label: '每日过期' },
  { value: 'manual_adjust', label: '手动调整' },
];

const typeLabel = (value: string): string =>
  LEDGER_TYPE_OPTIONS.find((o) => o.value === value)?.label || value;

/** ISO 时间 → 本地可读（无时区后缀按 UTC 处理由 Date 兜底；后端存 UTC） */
const formatTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
};

const PAGE_SIZE = 20;

export const CreditsLedgerModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const user = useAuthStore((s) => s.user);
  // 顶部余额：总余额 = 每日积分 + 订阅/永久池（后端扣费/发放同口径）
  const totalBalance = (user?.points ?? 0) + (user?.dailyPoints ?? 0);

  const [ledgerType, setLedgerType] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<LedgerItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(async (targetPage: number, targetType: string) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await creditsApi.getLedger(targetPage, PAGE_SIZE, targetType || undefined);
      // 竞态丢弃：仅渲染最后一次请求结果（快速切换筛选/翻页场景）
      if (requestId !== requestIdRef.current) return;
      setItems(result.items);
      setTotal(result.total);
      setPage(result.page);
    } catch (e: any) {
      if (requestId !== requestIdRef.current) return;
      setError(e?.message || '加载积分明细失败');
      setItems([]);
      setTotal(0);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPage(page, ledgerType);
  }, [fetchPage, page, ledgerType]);

  // 打开即对账：顶栏余额读 authStore 快照（登录时加载，生成扣费后可能滞后），进页强制刷新
  useEffect(() => {
    void useAuthStore.getState().refreshPoints();
  }, []);

  // ESC 关闭 + body 滚动锁定（MembershipModal 同款）
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleTypeChange = (value: string) => {
    setLedgerType(value);
    setPage(1);
  };

  return createPortal(
    <div className="fixed inset-0 z-[250] overflow-y-auto animate-in fade-in duration-150" style={{ backgroundColor: '#121212' }}>
      {/* 顶栏：返回 + 标题 + 当前余额（对齐会员页 ModalTopBar 视觉基准） */}
      <div className="sticky top-0 z-10 border-b border-white/[0.08]" style={{ backgroundColor: '#121212' }}>
        <div className="mx-auto flex h-14 w-full max-w-[960px] items-center gap-3 px-4 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            title="返回"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex-1 text-[15px] font-medium text-white">积分明细</div>
          <div className="flex items-center gap-1.5 text-[13px] text-white/70">
            <img src="/icons/home/token-icon.svg" alt="" width={14} height={14} draggable={false} />
            <span className="tabular-nums font-medium text-white">{totalBalance}</span>
            <span className="text-white/45">当前余额</span>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[960px] px-4 pb-20 sm:px-6">
        {/* 类型筛选：单行横向滚动标签组（值集对齐服务端 ledger_type 等值筛选） */}
        <div className="mt-6 flex items-center gap-2 overflow-x-auto pb-1">
          {LEDGER_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleTypeChange(opt.value)}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-[12px] font-medium transition-colors ${
                ledgerType === opt.value
                  ? 'bg-white text-black'
                  : 'bg-[#222] text-white/60 hover:bg-[#2a2a2a] hover:text-white/85'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* 列表卡片 */}
        <div className="mt-4 overflow-hidden rounded-xl border border-white/[0.08]" style={{ backgroundColor: '#181818' }}>
          {/* 表头 */}
          <div className="flex items-center gap-4 border-b border-white/[0.06] px-5 py-3 text-[11px] text-white/40">
            <span className="w-[88px] shrink-0">类型</span>
            <span className="min-w-0 flex-1">说明</span>
            <span className="w-[132px] shrink-0 text-right">变动 / 余额</span>
            <span className="w-[136px] shrink-0 text-right">时间</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-[13px] text-white/45">
              <Loader2 size={16} className="animate-spin" />
              加载中…
            </div>
          ) : error ? (
            <div className="py-16 text-center text-[13px] text-white/45">
              {error}
              <button
                type="button"
                onClick={() => void fetchPage(page, ledgerType)}
                className="ml-3 rounded-lg bg-white/10 px-3 py-1 text-[12px] text-white/80 transition-colors hover:bg-white/15"
              >
                重试
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-[13px] text-white/45">暂无积分流水记录</div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-4 border-b border-white/[0.04] px-5 py-3.5 text-[13px] last:border-b-0 hover:bg-white/[0.02]"
              >
                {/* 类型标签：入账绿系 / 扣减红系 */}
                <span className="w-[88px] shrink-0">
                  <span
                    className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${
                      item.amount >= 0 ? 'bg-[#123a2a] text-[#4ade80]' : 'bg-[#3a1d24] text-[#f87171]'
                    }`}
                  >
                    {typeLabel(item.ledgerType)}
                  </span>
                </span>
                {/* 说明：优先 reason，其次模型 id，再退 ref_id 摘要 */}
                <span className="min-w-0 flex-1 truncate text-white/75" title={item.reason || item.modelId || item.refId || ''}>
                  {item.reason || item.modelId || item.refId || '—'}
                </span>
                <span className="w-[132px] shrink-0 text-right tabular-nums">
                  <span className={item.amount >= 0 ? 'text-[#4ade80]' : 'text-[#f87171]'}>
                    {item.amount >= 0 ? `+${item.amount}` : `${item.amount}`}
                  </span>
                  <span className="ml-2 text-white/45">余额 {item.balanceAfter}</span>
                </span>
                <span className="w-[136px] shrink-0 text-right text-[12px] text-white/40 tabular-nums">
                  {formatTime(item.createdAt)}
                </span>
              </div>
            ))
          )}
        </div>

        {/* 分页：上一页 / 页码 / 下一页（total>0 才显示） */}
        {total > 0 && (
          <div className="mt-4 flex items-center justify-center gap-3 text-[12px] text-white/50">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent"
              title="上一页"
            >
              <ChevronLeft size={15} />
            </button>
            <span className="tabular-nums">
              第 {page} / {totalPages} 页 · 共 {total} 条
            </span>
            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent"
              title="下一页"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
